import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import { useResponsive } from '@/hooks/useResponsive';

import { getDataTestId } from '@/utils/browser';

import { Icon } from '../../Icon/Icon';
import { InfoDrawerProps } from './Drawer.interface';

import { ActionBar } from '../../Layout/Layout.styled';
import {
  InfoDrawerContainer,
  InfoModalContainer,
  StyledInfoDrawer,
} from './Drawer.styled';

export function InfoDrawer({
  title,
  description,
  drawerImage,
  content,
  primaryAction,
  secondaryAction,
  showHeader = true,
  primaryText = 'Yes',
  secondaryText = 'Cancel',
  primaryButtonProps,
  secondaryButtonProps,
  containerProps = {},
  onClose,
}: InfoDrawerProps) {
  const theme = useTheme();
  const { isDesktop, isTablet } = useResponsive();

  if (isDesktop || isTablet) {
    return (
      <Modal open hideBackdrop={false}>
        <InfoModalContainer {...containerProps}>
          {showHeader && (
            <Stack
              alignItems="flex-start"
              justifyContent="space-between"
              mb={title ? 4 : 0}
            >
              <Typography variant="h4">{title}</Typography>
              <Box mt={-0.5} mr={-0.5}>
                <IconButton onClick={onClose}>
                  <Icon name="close" color={theme => theme.palette.grey[700]} />
                </IconButton>
              </Box>
            </Stack>
          )}
          {drawerImage}
          {description && (
            <Typography variant="body2" color={theme.palette.grey[800]} mb={4}>
              {description}
            </Typography>
          )}
          {content}
          <ActionBar growContainedOnly>
            {secondaryAction && (
              <Button
                variant="outlined"
                {...getDataTestId(`info-secondary-button`)}
                onClick={secondaryAction}
                {...secondaryButtonProps}
              >
                {secondaryText}
              </Button>
            )}
            {primaryAction && (
              <Button
                {...getDataTestId(`info-primary-button`)}
                onClick={primaryAction}
                {...primaryButtonProps}
              >
                {primaryText}
              </Button>
            )}
          </ActionBar>
        </InfoModalContainer>
      </Modal>
    );
  }

  return (
    <StyledInfoDrawer
      open
      anchor="bottom"
      hideBackdrop={false}
      onClose={onClose}
    >
      <InfoDrawerContainer>
        {drawerImage}
        {title && (
          <Typography variant="h3" textAlign="center" mt={4.5} mb={1}>
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            variant="body2"
            textAlign="center"
            mb={4}
            color={theme.palette.text.secondary}
          >
            {description}
          </Typography>
        )}
        {content}
        <ActionBar growContainedOnly>
          {secondaryAction && (
            <Button
              {...getDataTestId(`info-secondary-button`)}
              onClick={secondaryAction}
              variant="outlined"
              {...secondaryButtonProps}
            >
              {secondaryText}
            </Button>
          )}
          {primaryAction && (
            <Button
              {...getDataTestId(`info-primary-button`)}
              onClick={primaryAction}
              {...primaryButtonProps}
            >
              {primaryText}
            </Button>
          )}
        </ActionBar>
      </InfoDrawerContainer>
    </StyledInfoDrawer>
  );
}
